package com.icebyte.printprofiletracker

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.automirrored.filled.List
import androidx.compose.material.icons.filled.AccountCircle
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.GridView
import androidx.compose.material.icons.filled.LightMode
import androidx.compose.material.icons.filled.Menu
import androidx.compose.material.icons.filled.Print
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.UploadFile
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.darkColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlin.random.Random
import kotlin.time.TimeSource

private object DashboardColors {
    val NavyBackground = Color(0xFF061C2E)
    val SlatePanel = Color(0xFF0E2336)
    val CardPanel = Color(0xFF122B3F)
    val CardPanelAlt = Color(0xFF10263a)
    val BorderColor = Color(0xFF2E5071)
    val AccentBlue = Color(0xFF68C3FF)
    val AccentBlueSoft = Color(0xFF4BA8FF)
    val SoftText = Color(0xFF9DB1C6)
    val LightText = Color(0xFFEAF6FF)
    val TagBlue = Color(0xFF1A5A8B)
    val TagBlueLight = Color(0xFF9BD3FF)
    val ButtonBlue = Color(0xFF5AA7FF)
}

private enum class PreviewType { BOAT, CUBE, CAT, GEAR, DRAGON }
private enum class ViewMode { GRID, LIST }

private data class ProfileEntry(
   val id: String,
   val title: String,
   val description: String,
   val material: String,
   val layerHeight: String,
   val printer: String,
   val notes: String,
   val resultCount: Int,
   val previewType: PreviewType,
   val photoUrl: String? = null,
)

@Composable
fun ProfileDashboardScreen() {
    // Load profiles from platform-backed store (IndexedDB on web). Start with empty list while loading.
    val profileList = remember { mutableStateListOf<ProfileEntry>() }

    // attachment presence collections (ids)
    val photoSet = remember { mutableStateListOf<String>() }
    val threeMfSet = remember { mutableStateListOf<String>() }
    val photoUrls = remember { mutableStateMapOf<String, String?>() }

    // Coroutine scope for loading/saving
    val scope = rememberCoroutineScope()
    var selectedSection by remember { mutableStateOf("Slice Profiles") }

    val loadProfilesFromStore: suspend () -> Unit = {
        try {
            val store = getProfileStore()
            val stored = store.loadAll()
            profileList.clear()
            photoSet.clear()
            threeMfSet.clear()
            photoUrls.clear()
            stored.forEach { sp ->
                val photoUrl = if (sp.hasPhoto) store.getAttachmentUrl(sp.id, "photo") else null
                profileList.add(
                    ProfileEntry(
                        id = sp.id,
                        title = sp.title,
                        description = sp.description,
                        material = sp.material,
                        layerHeight = sp.layerHeight,
                        printer = sp.printer,
                        notes = sp.notes,
                        resultCount = sp.resultCount,
                        previewType = PreviewType.BOAT, // placeholder
                        photoUrl = photoUrl,
                    )
                )
                if (sp.hasPhoto) {
                    photoSet.add(sp.id)
                    photoUrls[sp.id] = photoUrl
                }
                if (sp.hasThreeMf) threeMfSet.add(sp.id)
            }
        } catch (e: Throwable) {
            // ignore load errors for now
        }
    }

    LaunchedEffect(Unit) {
        loadProfilesFromStore()
    }

    LaunchedEffect(selectedSection) {
        if (selectedSection == "Slice Profiles") {
            loadProfilesFromStore()
        }
    }
    var selectedMaterialFilter by remember { mutableStateOf("All Filaments") }
    var selectedPrinterFilter by remember { mutableStateOf("All Printers") }
    var selectedTypeFilter by remember { mutableStateOf("All Profile Types") }
    var selectedSort by remember { mutableStateOf("Newest First") }
    var viewMode by remember { mutableStateOf(ViewMode.GRID) }
    var searchQuery by remember { mutableStateOf("") }
    var selectedProfileId by remember { mutableStateOf<String?>(null) }
    var profileDetail by remember { mutableStateOf<ProfileEntry?>(null) }
    var uploadDialogVisible by remember { mutableStateOf(false) }
    var fullProfileId by remember { mutableStateOf<String?>(null) }

    val visibleProfiles = remember(profileList, searchQuery, selectedSection, selectedMaterialFilter, selectedPrinterFilter, selectedTypeFilter, selectedSort) {
        val query = searchQuery.trim()
        profileList.asSequence()
            .filter { profile ->
                val matchesSearch = query.isEmpty() ||
                    profile.title.contains(query, ignoreCase = true) ||
                    profile.material.contains(query, ignoreCase = true) ||
                    profile.printer.contains(query, ignoreCase = true)
                val matchesMaterial = selectedMaterialFilter == "All Filaments" || profile.material == selectedMaterialFilter
                val matchesPrinter = selectedPrinterFilter == "All Printers" || profile.printer == selectedPrinterFilter
                val matchesType = selectedTypeFilter == "All Profile Types" || profile.title.contains(selectedTypeFilter, ignoreCase = true)
                matchesSearch && matchesMaterial && matchesPrinter && matchesType
            }
            .sortedWith { left, right ->
                when (selectedSort) {
                    "A-Z" -> left.title.compareTo(right.title)
                    "Z-A" -> right.title.compareTo(left.title)
                    "Oldest First" -> left.title.compareTo(right.title)
                    else -> right.title.compareTo(left.title)
                }
            }
            .toList()
    }

    val printerCount = profileList.map { it.printer }.distinct().size
    val totalResults = profileList.size

    MaterialTheme(
        colorScheme = darkColorScheme(
            primary = DashboardColors.AccentBlue,
            secondary = DashboardColors.AccentBlueSoft,
            background = DashboardColors.NavyBackground,
            surface = DashboardColors.SlatePanel,
            onPrimary = DashboardColors.LightText,
            onSurface = DashboardColors.LightText,
            onBackground = DashboardColors.LightText,
        )
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(DashboardColors.NavyBackground)
        ) {
            Row(modifier = Modifier.fillMaxSize()) {
                DashboardSidebar(
                    selectedSection = selectedSection,
                    onSectionSelected = { selectedSection = it },
                )
                Spacer(modifier = Modifier.width(18.dp))
                DashboardMainContent(
                   visibleProfiles = visibleProfiles,
                   searchQuery = searchQuery,
                   onSearchChanged = { searchQuery = it },
                   selectedMaterialFilter = selectedMaterialFilter,
                   onMaterialFilterChanged = { selectedMaterialFilter = it },
                   selectedPrinterFilter = selectedPrinterFilter,
                   onPrinterFilterChanged = { selectedPrinterFilter = it },
                   selectedTypeFilter = selectedTypeFilter,
                   onTypeFilterChanged = { selectedTypeFilter = it },
                   selectedSort = selectedSort,
                   onSortChanged = { selectedSort = it },
                   viewMode = viewMode,
                   onViewModeChanged = { viewMode = it },
                   selectedProfileId = selectedProfileId,
                   onProfileSelected = { selectedProfileId = it },
                   onProfileDetailsRequested = { profileDetail = it },
                   onUploadPressed = { uploadDialogVisible = true },
                   photoSet = photoSet,
                   threeMfSet = threeMfSet,
                   photoUrls = photoUrls,
                )
            }
        }
    }

    if (profileDetail != null) {
        ProfileDetailDialog(
            profile = profileDetail!!,
            onDismiss = { profileDetail = null },
            onViewProfile = { id -> fullProfileId = id }
        )
    }

    if (fullProfileId != null) {
        FullProfileDialog(
            profileId = fullProfileId!!,
            onClose = { fullProfileId = null },
            onProfileDeleted = {
                fullProfileId = null
                scope.launch { loadProfilesFromStore() }
            },
            reloadProfiles = { loadProfilesFromStore() }
        )
    }

    if (uploadDialogVisible) {
        UploadProfileDialog(
            onDismiss = { uploadDialogVisible = false },
            onSubmit = { title, material, printer, notes, photoFiles, threeMfFile ->
                scope.launch {
                    try {
                        val store = getProfileStore()
                        val nextId = "custom-${TimeSource.Monotonic.markNow().elapsedNow().inWholeMilliseconds}-${Random.nextLong(1_000_000_000L, Long.MAX_VALUE)}"
                        val profileToSave = StoredProfile(
                            id = nextId,
                            title = title.ifBlank { "Custom Profile" },
                            description = "Newly uploaded profile ready to test.",
                            material = material.ifBlank { "PLA" },
                            layerHeight = "0.20mm",
                            printer = printer.ifBlank { "Prusa MK4" },
                            notes = notes.ifBlank { "Fresh upload. Tune settings for your workflow." },
                            resultCount = 1,
                            hasPhoto = photoFiles.isNotEmpty(),
                            hasThreeMf = threeMfFile != null
                        )

                        store.saveProfile(profileToSave, photoFiles.ifEmpty { null }, threeMfFile)
                        loadProfilesFromStore()

                        selectedSection = "Slice Profiles"
                        selectedProfileId = nextId
                    } catch (e: Throwable) {
                        // ignore save errors for now
                    } finally {
                        uploadDialogVisible = false
                    }
                }
            }
        )
    }
}

@Composable
private fun DashboardSidebar(
    selectedSection: String,
    onSectionSelected: (String) -> Unit,
) {
    Box(
        modifier = Modifier
            .width(210.dp)
            .fillMaxHeight()
            .background(Color(0xFF081E31))
            .padding(horizontal = 14.dp, vertical = 18.dp)
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            SidebarBrand()

            SidebarNavItem(label = "Slice Profiles", selected = selectedSection == "Slice Profiles", modifier = Modifier.padding(vertical = 2.dp), onClick = { onSectionSelected("Slice Profiles") })
            SidebarNavItem(label = "Printers", selected = selectedSection == "Printers", modifier = Modifier.padding(vertical = 2.dp), onClick = { onSectionSelected("Printers") })
            SidebarNavItem(label = "Settings", selected = selectedSection == "Settings", modifier = Modifier.padding(vertical = 2.dp), onClick = { onSectionSelected("Settings") })
        }
    }
}

@Composable
private fun SidebarBrand() {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier
            .padding(start = 6.dp, bottom = 18.dp)
            .height(46.dp)
    ) {
        Box(
            modifier = Modifier
                .size(24.dp)
                .clip(RoundedCornerShape(7.dp))
                .background(Brush.linearGradient(listOf(Color(0xFF2CB2FF), Color(0xFF165DD9))))
        ) {
            Canvas(modifier = Modifier.fillMaxSize()) {
                val size = minOf(size.width, size.height)
                drawRoundRect(
                    color = Color(0xFF0F2B52),
                    topLeft = Offset(size * 0.16f, size * 0.18f),
                    size = Size(size * 0.68f, size * 0.62f),
                    cornerRadius = CornerRadius(6.dp.toPx(), 6.dp.toPx()),
                    style = Stroke(width = 2.dp.toPx())
                )
                drawRoundRect(
                    color = Color(0xFF0F2B52),
                    topLeft = Offset(size * 0.32f, size * 0.06f),
                    size = Size(size * 0.38f, size * 0.72f),
                    cornerRadius = CornerRadius(6.dp.toPx(), 6.dp.toPx()),
                    style = Stroke(width = 2.dp.toPx())
                )
            }
        }
    }
}

@Composable
private fun SidebarNavItem(
    label: String,
    selected: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
) {
    val bg = if (selected) Color(0xFF1A3550) else Color.Transparent
    val tint = if (selected) DashboardColors.LightText else DashboardColors.SoftText
    val shape = RoundedCornerShape(12.dp)

    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(42.dp)
            .clip(shape)
            .background(bg)
            .clickable(onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val iconModifier = Modifier.size(18.dp)
        when (label) {
            "Slice Profiles" -> Icon(Icons.Default.GridView, contentDescription = null, tint = tint, modifier = iconModifier)
            "All Profiles" -> Icon(Icons.AutoMirrored.Filled.List, contentDescription = null, tint = tint, modifier = iconModifier)
            "Upload Profile" -> Icon(Icons.Default.UploadFile, contentDescription = null, tint = tint, modifier = iconModifier)
            "Printers" -> Icon(Icons.Default.Print, contentDescription = null, tint = tint, modifier = iconModifier)
            "Settings" -> Icon(Icons.Default.Settings, contentDescription = null, tint = tint, modifier = iconModifier)
        }

        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = label,
            color = tint,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
            fontSize = 15.sp,
            modifier = Modifier.padding(start = 2.dp)
        )
    }
}

@Composable
private fun StatRow(label: String, value: String) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(label, color = DashboardColors.SoftText, fontSize = 12.sp)
        Text(value, color = DashboardColors.LightText, fontWeight = FontWeight.SemiBold, fontSize = 12.sp)
    }
    Spacer(modifier = Modifier.height(6.dp))
}

@Composable
private fun DashboardMainContent(
    visibleProfiles: List<ProfileEntry>,
    searchQuery: String,
    onSearchChanged: (String) -> Unit,
    selectedMaterialFilter: String,
    onMaterialFilterChanged: (String) -> Unit,
    selectedPrinterFilter: String,
    onPrinterFilterChanged: (String) -> Unit,
    selectedTypeFilter: String,
    onTypeFilterChanged: (String) -> Unit,
    selectedSort: String,
    onSortChanged: (String) -> Unit,
    viewMode: ViewMode,
    onViewModeChanged: (ViewMode) -> Unit,
    selectedProfileId: String?,
    onProfileSelected: (String) -> Unit,
    onProfileDetailsRequested: (ProfileEntry) -> Unit,
    onUploadPressed: () -> Unit,
    photoSet: List<String>,
    threeMfSet: List<String>,
    photoUrls: Map<String, String?>,
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(top = 18.dp, end = 20.dp, bottom = 18.dp)
    ) {
        TopToolbar(searchQuery, onSearchChanged)
        Spacer(modifier = Modifier.height(18.dp))
        HeaderBanner()
        Spacer(modifier = Modifier.height(18.dp))
        StatCardsRow(totalProfiles = visibleProfiles.size, totalPrinters = visibleProfiles.map { it.printer }.distinct().size)
        Spacer(modifier = Modifier.height(18.dp))
        FilterBar(
            selectedMaterialFilter = selectedMaterialFilter,
            onMaterialFilterChanged = onMaterialFilterChanged,
            selectedPrinterFilter = selectedPrinterFilter,
            onPrinterFilterChanged = onPrinterFilterChanged,
            selectedTypeFilter = selectedTypeFilter,
            onTypeFilterChanged = onTypeFilterChanged,
            selectedSort = selectedSort,
            onSortChanged = onSortChanged,
            viewMode = viewMode,
            onViewModeChanged = onViewModeChanged,
        )
        Spacer(modifier = Modifier.height(18.dp))
        ContentGrid(
            profiles = visibleProfiles,
            viewMode = viewMode,
            selectedProfileId = selectedProfileId,
            onProfileSelected = onProfileSelected,
            onProfileDetailsRequested = onProfileDetailsRequested,
            onUploadPressed = onUploadPressed,
            photoSet = photoSet,
            threeMfSet = threeMfSet,
            photoUrls = photoUrls,
        )
        Spacer(modifier = Modifier.height(18.dp))
        PaginationFooter(visibleCount = visibleProfiles.size)
    }
}

@Composable
private fun TopToolbar(searchQuery: String, onSearchChanged: (String) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        SearchBox(
            value = searchQuery,
            onValueChange = onSearchChanged,
            modifier = Modifier.weight(1f),
        )

        Spacer(modifier = Modifier.width(14.dp))
        Row(verticalAlignment = Alignment.CenterVertically) {
            ActionIconButton(icon = Icons.Default.LightMode)
            Spacer(modifier = Modifier.width(12.dp))
            Box(
                modifier = Modifier
                    .size(34.dp)
                    .clip(CircleShape)
                    .background(Color(0xFF1F4C6D))
            ) {
                Icon(Icons.Default.AccountCircle, contentDescription = null, tint = DashboardColors.LightText, modifier = Modifier.fillMaxSize())
            }
            Spacer(modifier = Modifier.width(8.dp))
            Text("Nathan", color = DashboardColors.LightText, fontSize = 15.sp, fontWeight = FontWeight.Medium)
            Icon(Icons.Default.Menu, contentDescription = null, tint = DashboardColors.SoftText, modifier = Modifier.size(18.dp))
        }
    }
}

@Composable
private fun SearchBox(value: String, onValueChange: (String) -> Unit, modifier: Modifier = Modifier) {
    val containerColor = Color(0xFF0F2A3D).copy(alpha = 0.9f)

    Box(
        modifier = modifier
            .height(42.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(containerColor)
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f), RoundedCornerShape(14.dp))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Icon(
                Icons.Default.Search,
                contentDescription = null,
                tint = DashboardColors.SoftText,
                modifier = Modifier.size(18.dp)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Box(modifier = Modifier.weight(1f)) {
                if (value.isEmpty()) {
                    Text(
                        text = "Search profiles...",
                        color = DashboardColors.SoftText,
                        fontSize = 16.sp
                    )
                }
                BasicTextField(
                    value = value,
                    onValueChange = onValueChange,
                    singleLine = true,
                    cursorBrush = SolidColor(DashboardColors.AccentBlue),
                    textStyle = TextStyle(
                        color = DashboardColors.LightText,
                        fontSize = 16.sp
                    ),
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}

@Composable
private fun ActionIconButton(icon: ImageVector) {
    Box(
        modifier = Modifier
            .size(36.dp)
            .clip(CircleShape)
            .background(Color(0xFF11283B))
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.6f), CircleShape)
            .clickable { },
        contentAlignment = Alignment.Center
    ) {
        Icon(icon, contentDescription = null, tint = DashboardColors.SoftText, modifier = Modifier.size(18.dp))
    }
}

@Composable
private fun HeaderBanner() {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(155.dp)
            .clip(RoundedCornerShape(20.dp))
            .background(
                Brush.linearGradient(
                    listOf(
                        Color(0xFF0D2A46),
                        Color(0xFF12385A),
                        Color(0xFF0B233B)
                    )
                )
            )
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f), RoundedCornerShape(20.dp))
            .padding(horizontal = 24.dp, vertical = 16.dp)
    ) {
        Column(modifier = Modifier.fillMaxSize()) {
            Text(
                text = "Slice Profiles",
                color = DashboardColors.LightText,
                fontSize = 42.sp,
                fontWeight = FontWeight.Bold,
                lineHeight = 46.sp
            )
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                text = "Upload, organize, and track your 3D printing slice profiles.",
                color = DashboardColors.SoftText,
                fontSize = 17.sp
            )
        }

        Canvas(modifier = Modifier.fillMaxSize()) {
            val base = size.height
            val right = size.width
            val y1 = base * 0.7f
            val y2 = base * 0.82f
            drawPath(
                path = Path().apply {
                    moveTo(0f, y2)
                    lineTo(right * 0.15f, y1)
                    lineTo(right * 0.25f, y2)
                    lineTo(right * 0.45f, y1)
                    lineTo(right * 0.58f, y2)
                    lineTo(right * 0.72f, y1)
                    lineTo(right * 0.86f, y2)
                    lineTo(right, y2)
                    lineTo(right, base)
                    lineTo(0f, base)
                    close()
                },
                color = Color(0xFF1B3A58).copy(alpha = 0.9f)
            )
            drawPath(
                path = Path().apply {
                    moveTo(0f, base)
                    lineTo(right * 0.18f, y2)
                    lineTo(right * 0.4f, base)
                    lineTo(right * 0.63f, y2)
                    lineTo(right, base)
                    lineTo(right, base)
                    lineTo(0f, base)
                    close()
                },
                color = Color(0xFF0C243A).copy(alpha = 0.9f)
            )
        }
    }
}

@Composable
private fun StatCardsRow(totalProfiles: Int, totalPrinters: Int) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        StatCard(iconText = "▣", label = "Slice Profiles", value = totalProfiles.toString(), modifier = Modifier.weight(1f))
        StatCard(iconText = "🖨", label = "Printers", value = totalPrinters.toString(), modifier = Modifier.weight(1f))
        StatCard(iconText = "▤", label = "Total Print Results", value = "142", modifier = Modifier.weight(1f))
        StatCard(iconText = "◔", label = "Successful Prints", value = "92%", modifier = Modifier.weight(1f))
    }
}

@Composable
private fun StatCard(iconText: String, label: String, value: String, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .height(88.dp)
            .clip(RoundedCornerShape(14.dp))
            .background(Color(0xFF11283D).copy(alpha = 0.92f))
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.4f), RoundedCornerShape(14.dp))
            .padding(horizontal = 16.dp, vertical = 12.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .background(Color(0xFF1A3550)),
                contentAlignment = Alignment.Center
            ) {
                Text(iconText, color = DashboardColors.AccentBlue, fontSize = 16.sp)
            }
            Spacer(modifier = Modifier.width(12.dp))
            Text(value, color = DashboardColors.LightText, fontSize = 26.sp, fontWeight = FontWeight.Bold)
        }
        Text(
            label,
            color = DashboardColors.SoftText,
            fontSize = 13.sp,
            modifier = Modifier.align(Alignment.BottomStart)
        )
    }
}

@Composable
private fun FilterBar(
    selectedMaterialFilter: String,
    onMaterialFilterChanged: (String) -> Unit,
    selectedPrinterFilter: String,
    onPrinterFilterChanged: (String) -> Unit,
    selectedTypeFilter: String,
    onTypeFilterChanged: (String) -> Unit,
    selectedSort: String,
    onSortChanged: (String) -> Unit,
    viewMode: ViewMode,
    onViewModeChanged: (ViewMode) -> Unit,
) {
    val materialOptions = listOf("All Filaments", "PLA", "PLA+", "PETG", "ABS")
    val printerOptions = listOf("All Printers", "Ender 3 V3", "Bambu P1S", "Ender 3 S1", "Prusa MK4")
    val typeOptions = listOf("All Profile Types", "Benchy", "Calibration", "Articulated", "Functional")
    val sortOptions = listOf("Newest First", "Oldest First", "A-Z", "Z-A")

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        FilterDropdown(
            label = selectedMaterialFilter,
            options = materialOptions,
            onOptionSelected = onMaterialFilterChanged,
            modifier = Modifier.weight(1f, fill = false)
        )
        FilterDropdown(
            label = selectedPrinterFilter,
            options = printerOptions,
            onOptionSelected = onPrinterFilterChanged,
            modifier = Modifier.weight(1f, fill = false)
        )
        FilterDropdown(
            label = selectedTypeFilter,
            options = typeOptions,
            onOptionSelected = onTypeFilterChanged,
            modifier = Modifier.weight(1f, fill = false)
        )
        FilterDropdown(
            label = selectedSort,
            options = sortOptions,
            onOptionSelected = onSortChanged,
            modifier = Modifier.weight(1f, fill = false)
        )
        ToggleIconButton(icon = Icons.Default.GridView, active = viewMode == ViewMode.GRID, onClick = { onViewModeChanged(ViewMode.GRID) })
        ToggleIconButton(icon = Icons.AutoMirrored.Filled.List, active = viewMode == ViewMode.LIST, onClick = { onViewModeChanged(ViewMode.LIST) })
    }
}

@Composable
private fun FilterDropdown(
    label: String,
    options: List<String>,
    onOptionSelected: (String) -> Unit,
    modifier: Modifier = Modifier,
) {
    var expanded by remember { mutableStateOf(false) }

    Box(modifier = modifier) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(42.dp)
                .clip(RoundedCornerShape(12.dp))
                .background(Color(0xFF11283D).copy(alpha = 0.9f))
                .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
                .clickable { expanded = true }
                .padding(horizontal = 12.dp),
            contentAlignment = Alignment.CenterStart
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(label, color = DashboardColors.LightText, fontSize = 14.sp, maxLines = 1)
                Icon(Icons.Default.ArrowDropDown, contentDescription = null, tint = DashboardColors.SoftText, modifier = Modifier.size(16.dp))
            }
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(Color(0xFF0F2335))
        ) {
            options.forEach { option ->
                DropdownMenuItem(
                    text = { Text(option, color = DashboardColors.LightText) },
                    onClick = {
                        onOptionSelected(option)
                        expanded = false
                    }
                )
            }
        }
    }
}

@Composable
private fun ToggleIconButton(icon: ImageVector, active: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(42.dp)
            .clip(RoundedCornerShape(10.dp))
            .background(Color(0xFF122B3F).copy(alpha = 0.85f))
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.4f), RoundedCornerShape(10.dp))
            .clickable(onClick = onClick),
        contentAlignment = Alignment.Center
    ) {
        Icon(icon, contentDescription = null, tint = if (active) DashboardColors.AccentBlue else DashboardColors.SoftText, modifier = Modifier.size(18.dp))
    }
}

@Composable
private fun ContentGrid(
    profiles: List<ProfileEntry>,
    viewMode: ViewMode,
    selectedProfileId: String?,
    onProfileSelected: (String) -> Unit,
    onProfileDetailsRequested: (ProfileEntry) -> Unit,
    onUploadPressed: () -> Unit,
    photoSet: List<String>,
    threeMfSet: List<String>,
    photoUrls: Map<String, String?>,
) {
    if (viewMode == ViewMode.LIST) {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            profiles.forEach { profile ->
                ProfileCard(
                    profile = profile,
                    selected = selectedProfileId == profile.id,
                    onClick = {
                        onProfileSelected(profile.id)
                        onProfileDetailsRequested(profile)
                    },
                    modifier = Modifier.fillMaxWidth(),
                    photoSet = photoSet,
                    threeMfSet = threeMfSet,
                    photoUrl = photoUrls[profile.id],
                )
            }
        }
        return
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            val rows = profiles.chunked(3)
            if (rows.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().height(120.dp), contentAlignment = Alignment.Center) {
                    Text("No matching profiles", color = DashboardColors.SoftText)
                }
            } else {
                rows.forEach { row ->
                    Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                        row.forEach { profile ->
                            ProfileCard(
                                profile = profile,
                                selected = selectedProfileId == profile.id,
                                onClick = {
                                    onProfileSelected(profile.id)
                                    onProfileDetailsRequested(profile)
                                },
                                modifier = Modifier.weight(1f),
                                photoSet = photoSet,
                                threeMfSet = threeMfSet,
                                photoUrl = photoUrls[profile.id],
                            )
                        }
                        if (row.size < 3) {
                            repeat(3 - row.size) { Spacer(modifier = Modifier.weight(1f)) }
                        }
                    }
                }
            }
        }

        UploadCard(onUploadPressed = onUploadPressed, modifier = Modifier.width(260.dp))
    }
}

@Composable
private fun ProfileCard(
    profile: ProfileEntry,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    photoSet: List<String>,
    threeMfSet: List<String>,
    photoUrl: String?,
) {
    Card(
        modifier = modifier
            .heightIn(min = 300.dp)
            .fillMaxWidth()
            .clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = if (selected) Color(0xFF15324D) else DashboardColors.CardPanel.copy(alpha = 0.9f)),
        shape = RoundedCornerShape(18.dp),
        border = androidx.compose.foundation.BorderStroke(1.dp, if (selected) DashboardColors.AccentBlue.copy(alpha = 0.6f) else DashboardColors.BorderColor.copy(alpha = 0.35f))
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(120.dp)
                    .background(Color(0xFF0D1F2C)),
                contentAlignment = Alignment.Center
            ) {
                if (photoUrl != null) {
                    AsyncImage(
                        model = imageUrlForDisplay(photoUrl),
                        contentDescription = "Profile photo for ${profile.title}",
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop,
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .size(92.dp)
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFF22333F)),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("Photo", color = DashboardColors.SoftText)
                    }
                }
                if (photoSet.contains(profile.id)) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopStart)
                            .padding(8.dp)
                            .clip(RoundedCornerShape(6.dp))
                            .background(DashboardColors.TagBlue)
                            .padding(horizontal = 6.dp, vertical = 4.dp)
                    ) {
                        Text("Photo attached", color = DashboardColors.TagBlueLight, fontSize = 11.sp)
                    }
                }
                if (threeMfSet.contains(profile.id)) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(8.dp)
                            .clip(RoundedCornerShape(6.dp))
                            .background(DashboardColors.TagBlue)
                            .padding(horizontal = 6.dp, vertical = 4.dp)
                    ) {
                        Text(".3mf", color = DashboardColors.TagBlueLight, fontSize = 11.sp)
                    }
                }
            }

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top
            ) {
                Text(
                    text = profile.title,
                    color = DashboardColors.LightText,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f).padding(end = 8.dp)
                )
                IconButton(onClick = onClick, modifier = Modifier.size(24.dp)) {
                    Text("⋯", color = DashboardColors.SoftText, fontSize = 22.sp)
                }
            }

            Text(
                text = profile.description,
                color = DashboardColors.SoftText,
                fontSize = 13.sp,
                lineHeight = 18.sp,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp)
            )

            Spacer(modifier = Modifier.height(10.dp))

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .height(28.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(DashboardColors.TagBlue.copy(alpha = 0.9f))
                        .padding(horizontal = 10.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(profile.material, color = DashboardColors.TagBlueLight, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
                }
                Text(
                    text = profile.layerHeight,
                    color = DashboardColors.LightText,
                    fontWeight = FontWeight.Medium,
                    fontSize = 13.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(modifier = Modifier.size(16.dp).clip(CircleShape).background(Color(0xFF5AA7FF)))
                Spacer(modifier = Modifier.width(8.dp))
                Text(
                    profile.printer,
                    color = DashboardColors.SoftText,
                    fontSize = 12.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            Spacer(modifier = Modifier.height(10.dp))

            Text(
                text = profile.notes,
                color = DashboardColors.SoftText,
                fontSize = 12.sp,
                lineHeight = 18.sp,
                maxLines = 3,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 14.dp)
            )

            Spacer(modifier = Modifier.height(10.dp))

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(52.dp)
                    .background(Color(0xFF102B3D))
                    .padding(horizontal = 14.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(
                    modifier = Modifier.weight(1f),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(16.dp)
                            .background(Color(0xFF203D59), RoundedCornerShape(4.dp)),
                        contentAlignment = Alignment.Center
                    ) {
                        Text("▣", color = DashboardColors.AccentBlue, fontSize = 10.sp)
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "${profile.resultCount} print results",
                        color = DashboardColors.LightText,
                        fontSize = 12.sp,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.fillMaxWidth()
                    )
                }
                Icon(Icons.AutoMirrored.Filled.ArrowForward, contentDescription = null, tint = DashboardColors.AccentBlue, modifier = Modifier.size(18.dp))
            }
        }
    }
}

@Composable
private fun UploadCard(modifier: Modifier = Modifier, onUploadPressed: () -> Unit) {
    Card(
        modifier = modifier.height(325.dp).fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = DashboardColors.CardPanel.copy(alpha = 0.92f)),
        shape = RoundedCornerShape(18.dp),
        border = androidx.compose.foundation.BorderStroke(1.dp, DashboardColors.BorderColor.copy(alpha = 0.35f))
    ) {
        Column(
            modifier = Modifier.fillMaxSize().padding(horizontal = 18.dp, vertical = 16.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Spacer(modifier = Modifier.height(18.dp))
            Box(
                modifier = Modifier
                    .size(88.dp)
                    .clip(CircleShape)
                    .background(Brush.linearGradient(listOf(Color(0xFF2A7ED8), Color(0xFF0E375B))))
                    .border(1.dp, Color(0xFF5FB8FF).copy(alpha = 0.6f), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Text("↑", color = DashboardColors.LightText, fontSize = 34.sp, fontWeight = FontWeight.Bold)
            }
            Spacer(modifier = Modifier.height(18.dp))
            Text("Upload a New Profile", color = DashboardColors.LightText, fontSize = 24.sp, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
            Spacer(modifier = Modifier.height(8.dp))
            Text(
                "Add a slice profile with photos and notes\nto keep track of your results.",
                color = DashboardColors.SoftText,
                fontSize = 14.sp,
                lineHeight = 20.sp,
                textAlign = TextAlign.Center
            )
            Spacer(modifier = Modifier.weight(1f))
            Button(
                onClick = onUploadPressed,
                modifier = Modifier.fillMaxWidth().height(44.dp),
                colors = ButtonDefaults.buttonColors(containerColor = DashboardColors.ButtonBlue)
            ) {
                Text("Upload Profile", color = DashboardColors.LightText, fontWeight = FontWeight.SemiBold)
            }
        }
    }
}

@Composable
private fun ProfileDetailDialog(profile: ProfileEntry, onDismiss: () -> Unit, onViewProfile: (String) -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.fillMaxWidth().padding(24.dp),
            shape = RoundedCornerShape(22.dp),
            colors = CardDefaults.cardColors(containerColor = Color(0xFF11283D)),
            border = androidx.compose.foundation.BorderStroke(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f))
        ) {
            Column(modifier = Modifier.fillMaxWidth().padding(20.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = profile.title,
                        color = DashboardColors.LightText,
                        fontSize = 22.sp,
                        fontWeight = FontWeight.Bold,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f).padding(end = 12.dp)
                    )
                    IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                        Text("✕", color = DashboardColors.SoftText, fontSize = 18.sp)
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(140.dp)
                        .clip(RoundedCornerShape(16.dp))
                        .background(Color(0xFF0C1A2A))
                ) {
                    if (profile.photoUrl != null) {
                        AsyncImage(
                            model = imageUrlForDisplay(profile.photoUrl),
                            contentDescription = "Profile photo for ${profile.title}",
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                        )
                    } else {
                        PreviewArtwork(type = profile.previewType)
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    InfoPill(profile.material)
                    InfoPill(profile.layerHeight)
                    InfoPill(profile.printer)
                }

                Spacer(modifier = Modifier.height(16.dp))

                Text(
                    text = profile.description,
                    color = DashboardColors.LightText,
                    fontSize = 15.sp,
                    lineHeight = 22.sp,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(12.dp))

                Text(
                    text = "Notes",
                    color = DashboardColors.SoftText,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = 0.4.sp
                )
                Text(
                    text = profile.notes,
                    color = DashboardColors.SoftText,
                    fontSize = 13.sp,
                    lineHeight = 20.sp,
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(16.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "${profile.resultCount} print results",
                        color = DashboardColors.LightText,
                        fontSize = 14.sp,
                        fontWeight = FontWeight.SemiBold
                    )
                    Button(
                        onClick = { onViewProfile(profile.id) },
                        colors = ButtonDefaults.buttonColors(containerColor = DashboardColors.ButtonBlue)
                    ) {
                        Text("View profile")
                    }
                }
            }
        }
    }
}

@Composable
private fun FullProfileDialog(profileId: String, onClose: () -> Unit, onProfileDeleted: () -> Unit, reloadProfiles: suspend () -> Unit) {
    val scope = rememberCoroutineScope()
    var profile by remember { mutableStateOf<StoredProfile?>(null) }
    var threeMfSummary by remember { mutableStateOf<String?>(null) }
    var threeMfUrl by remember { mutableStateOf<String?>(null) }
    var photoUrl by remember { mutableStateOf<String?>(null) }
    var editing by remember { mutableStateOf(false) }
    var editTitle by remember { mutableStateOf("") }
    var editMaterial by remember { mutableStateOf("") }
    var editPrinter by remember { mutableStateOf("") }
    var editNotes by remember { mutableStateOf("") }
    var newPhotoFiles by remember { mutableStateOf<List<Any>>(emptyList()) }
    var newThreeMfFile by remember { mutableStateOf<Any?>(null) }
    var validationMsg by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(profileId) {
        try {
            val store = getProfileStore()
            val list = store.loadAll()
            val found = list.firstOrNull { it.id == profileId }
            profile = found
            if (found != null) {
                editTitle = found.title
                editMaterial = found.material
                editPrinter = found.printer
                editNotes = found.notes
                photoUrl = if (found.hasPhoto) store.getAttachmentUrl(found.id, "photo") else null
                threeMfUrl = if (found.hasThreeMf) store.getAttachmentUrl(found.id, "threeMf") else null
                threeMfSummary = threeMfUrl?.let { fetchThreeMfSummary(it) }
            }
        } catch (_: Throwable) {
            // ignore
        }
    }

    Dialog(onDismissRequest = onClose) {
        Card(
            modifier = Modifier.fillMaxWidth().padding(18.dp),
            shape = RoundedCornerShape(16.dp),
            colors = CardDefaults.cardColors(containerColor = Color(0xFF0F2335)),
            border = androidx.compose.foundation.BorderStroke(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f))
        ) {
            Column(modifier = Modifier.fillMaxWidth().padding(18.dp)) {
                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                    Text(profile?.title ?: "Profile", color = DashboardColors.LightText, fontSize = 20.sp, fontWeight = FontWeight.Bold)
                    Row {
                        if (!editing) {
                            Button(onClick = { editing = true }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF223B52))) { Text("Edit") }
                            Spacer(modifier = Modifier.width(8.dp))
                        }
                        Button(onClick = {
                            // delete
                            scope.launch {
                                try {
                                    val store = getProfileStore()
                                    val ok = store.deleteProfile(profileId)
                                    if (ok) {
                                        onProfileDeleted()
                                        onClose()
                                    } else {
                                        validationMsg = "Failed to delete profile"
                                    }
                                } catch (_: Throwable) {
                                    validationMsg = "Error deleting"
                                }
                            }
                        }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFB94E4E))) { Text("Delete") }
                        Spacer(modifier = Modifier.width(8.dp))
                        IconButton(onClick = onClose) { Text("✕", color = DashboardColors.SoftText) }
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                Box(modifier = Modifier.fillMaxWidth().height(140.dp).clip(RoundedCornerShape(12.dp)).background(Color(0xFF0C1A2A))) {
                    val currentPhotoUrl = photoUrl
                    if (currentPhotoUrl != null) {
                        AsyncImage(model = imageUrlForDisplay(currentPhotoUrl), contentDescription = null, modifier = Modifier.fillMaxSize(), contentScale = ContentScale.Crop)
                    } else {
                        PreviewArtwork(type = PreviewType.BOAT)
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                if (editing) {
                    FormField(label = "Profile name") { LabeledTextInput(value = editTitle, onValueChange = { editTitle = it }, placeholder = "Profile name") }
                    Spacer(modifier = Modifier.height(8.dp))
                    FormField(label = "Material") { LabeledTextInput(value = editMaterial, onValueChange = { editMaterial = it }, placeholder = "Material") }
                    Spacer(modifier = Modifier.height(8.dp))
                    FormField(label = "Printer") { LabeledTextInput(value = editPrinter, onValueChange = { editPrinter = it }, placeholder = "Printer") }
                    Spacer(modifier = Modifier.height(8.dp))
                    FormField(label = "Notes") { LabeledTextInput(value = editNotes, onValueChange = { editNotes = it }, placeholder = "Notes") }

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        val scope2 = rememberCoroutineScope()
                        Button(onClick = {
                            scope2.launch {
                                val picked = pickFiles("image/*", true)
                                if (picked != null) newPhotoFiles = picked.toList()
                            }
                        }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF223B52))) { Text("Replace photos") }
                        Button(onClick = {
                            scope2.launch {
                                val picked = pickFiles(".3mf,application/octet-stream,application/*", false)
                                newThreeMfFile = picked?.firstOrNull()
                            }
                        }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF223B52))) { Text("Replace 3MF") }
                    }

                    Spacer(modifier = Modifier.height(12.dp))

                    Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
                        Button(onClick = { editing = false }, colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent)) { Text("Cancel") }
                        Spacer(modifier = Modifier.width(8.dp))
                        Button(onClick = {
                            // Save edits
                            if (editTitle.isBlank() || editMaterial.isBlank() || editPrinter.isBlank()) {
                                validationMsg = "Name, material and printer are required"
                                return@Button
                            }
                            validationMsg = null
                            scope.launch {
                                try {
                                    val store = getProfileStore()
                                    val current = profile
                                    if (current != null) {
                                        val updated = current.copy(
                                            title = editTitle,
                                            material = editMaterial,
                                            printer = editPrinter,
                                            notes = editNotes
                                        )
                                        store.saveProfile(updated, newPhotoFiles.ifEmpty { null }, newThreeMfFile)
                                        reloadProfiles()
                                        editing = false
                                        // reload local
                                        val list = store.loadAll()
                                        profile = list.firstOrNull { it.id == profileId }
                                        if (profile != null) {
                                            photoUrl = if (profile!!.hasPhoto) store.getAttachmentUrl(profile!!.id, "photo") else null
                                            threeMfUrl = if (profile!!.hasThreeMf) store.getAttachmentUrl(profile!!.id, "threeMf") else null
                                            threeMfSummary = threeMfUrl?.let { fetchThreeMfSummary(it) }
                                        }
                                    }
                                } catch (_: Throwable) {
                                    validationMsg = "Error saving"
                                }
                            }
                        }, colors = ButtonDefaults.buttonColors(containerColor = DashboardColors.ButtonBlue)) { Text("Save") }
                    }
                } else {
                    Text(profile?.description ?: "", color = DashboardColors.SoftText)
                    Spacer(modifier = Modifier.height(8.dp))
                    Text("Notes:", color = DashboardColors.SoftText, fontWeight = FontWeight.SemiBold)
                    Text(profile?.notes ?: "", color = DashboardColors.SoftText)

                    Spacer(modifier = Modifier.height(12.dp))

                    if (threeMfUrl != null) {
                        Text(threeMfSummary ?: "3MF available (binary)", color = DashboardColors.LightText)
                        Spacer(modifier = Modifier.height(8.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            Button(onClick = { threeMfUrl?.let { openUrl(it) } }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF223B52))) { Text("Download 3MF") }
                            Button(onClick = {
                                // try to show parsed summary again
                                scope.launch {
                                    threeMfSummary = threeMfUrl?.let { fetchThreeMfSummary(it) }
                                }
                            }, colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF223B52))) { Text("Refresh parse") }
                        }
                    }

                    Spacer(modifier = Modifier.height(8.dp))

                    Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
                        Button(onClick = onClose, colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent)) { Text("Close") }
                    }
                }

                if (validationMsg != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(validationMsg ?: "", color = Color(0xFFFFB4B4))
                }
            }
        }
    }
}

@Composable
private fun UploadProfileDialog(
    onDismiss: () -> Unit,
    onSubmit: (String, String, String, String, List<Any>, Any?) -> Unit,
) {
    var title by remember { mutableStateOf("") }
    var material by remember { mutableStateOf("PLA") }
    var printer by remember { mutableStateOf("Prusa MK4") }
    var notes by remember { mutableStateOf("") }
    var selectedPhotoFiles by remember { mutableStateOf<List<Any>>(emptyList()) }
    var selectedThreeMfFile by remember { mutableStateOf<Any?>(null) }
    var validationMessage by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val isValid = title.isNotBlank() && material.isNotBlank() && printer.isNotBlank()

    Dialog(onDismissRequest = onDismiss) {
        Card(
            modifier = Modifier.fillMaxWidth().padding(24.dp),
            shape = RoundedCornerShape(22.dp),
            colors = CardDefaults.cardColors(containerColor = Color(0xFF11283D)),
            border = androidx.compose.foundation.BorderStroke(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f))
        ) {
            Column(modifier = Modifier.fillMaxWidth().padding(20.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text("Upload profile", color = DashboardColors.LightText, fontSize = 22.sp, fontWeight = FontWeight.Bold)
                    IconButton(onClick = onDismiss, modifier = Modifier.size(28.dp)) {
                        Text("✕", color = DashboardColors.SoftText, fontSize = 18.sp)
                    }
                }

                Spacer(modifier = Modifier.height(18.dp))

                FormField(label = "Profile name") {
                    LabeledTextInput(value = title, onValueChange = { title = it }, placeholder = "e.g. Carbon Fiber Test")
                }
                Spacer(modifier = Modifier.height(12.dp))
                FormField(label = "Material") {
                    LabeledTextInput(value = material, onValueChange = { material = it }, placeholder = "PLA")
                }
                Spacer(modifier = Modifier.height(12.dp))
                FormField(label = "Printer") {
                    LabeledTextInput(value = printer, onValueChange = { printer = it }, placeholder = "Prusa MK4")
                }
                Spacer(modifier = Modifier.height(12.dp))
                FormField(label = "Notes") {
                    LabeledTextInput(value = notes, onValueChange = { notes = it }, placeholder = "Add notes for this profile")
                }

                Spacer(modifier = Modifier.height(12.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Button(
                        onClick = {
                            scope.launch {
                                val picked = pickFiles("image/*", true)
                                if (picked != null) selectedPhotoFiles = picked.toList()
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1A3550)),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("Select photos")
                    }
                    Button(
                        onClick = {
                            scope.launch {
                                val picked = pickFiles(".3mf,application/octet-stream,application/*", false)
                                selectedThreeMfFile = picked?.firstOrNull()
                            }
                        },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF1A3550)),
                        modifier = Modifier.weight(1f)
                    ) {
                        Text("Select 3MF")
                    }
                }

                Spacer(modifier = Modifier.height(8.dp))

                if (selectedPhotoFiles.isNotEmpty()) {
                    Text(
                        "Photos: ${selectedPhotoFiles.size} selected",
                        color = DashboardColors.AccentBlue,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(bottom = 4.dp)
                    )
                }
                if (selectedThreeMfFile != null) {
                    Text(
                        "3MF: selected",
                        color = DashboardColors.AccentBlue,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(bottom = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(18.dp))

                if (validationMessage != null) {
                    Text(
                        text = validationMessage!!,
                        color = Color(0xFFFFB4B4),
                        fontSize = 12.sp,
                        modifier = Modifier.padding(bottom = 8.dp)
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End
                ) {
                    Button(
                        onClick = onDismiss,
                        colors = ButtonDefaults.buttonColors(containerColor = Color.Transparent)
                    ) {
                        Text("Cancel", color = DashboardColors.SoftText)
                    }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        onClick = {
                            if (!isValid) {
                                validationMessage = "Profile name, material, and printer are required."
                                return@Button
                            }
                            validationMessage = null
                            onSubmit(title, material, printer, notes, selectedPhotoFiles, selectedThreeMfFile)
                        },
                        enabled = isValid,
                        colors = ButtonDefaults.buttonColors(containerColor = DashboardColors.ButtonBlue)
                    ) {
                        Text("Save profile")
                    }
                }
            }
        }
    }
}

@Composable
private fun FormField(label: String, content: @Composable () -> Unit) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(label, color = DashboardColors.SoftText, fontSize = 12.sp, fontWeight = FontWeight.SemiBold, modifier = Modifier.padding(bottom = 6.dp))
        content()
    }
}

@Composable
private fun LabeledTextInput(value: String, onValueChange: (String) -> Unit, placeholder: String) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(42.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(Color(0xFF0F2A3D).copy(alpha = 0.9f))
            .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.5f), RoundedCornerShape(12.dp))
            .padding(horizontal = 12.dp),
        contentAlignment = Alignment.CenterStart
    ) {
        if (value.isEmpty()) {
            Text(placeholder, color = DashboardColors.SoftText, fontSize = 14.sp)
        }
        BasicTextField(
            value = value,
            onValueChange = onValueChange,
            singleLine = true,
            cursorBrush = SolidColor(DashboardColors.AccentBlue),
            textStyle = TextStyle(color = DashboardColors.LightText, fontSize = 14.sp),
            modifier = Modifier.fillMaxWidth()
        )
    }
}

@Composable
private fun InfoPill(text: String) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(DashboardColors.TagBlue.copy(alpha = 0.9f))
            .padding(horizontal = 10.dp, vertical = 6.dp)
    ) {
        Text(text, color = DashboardColors.TagBlueLight, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}

@Composable
private fun PaginationFooter(visibleCount: Int) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text("Showing 1-$visibleCount of ${visibleCount.coerceAtLeast(1)} profiles", color = DashboardColors.SoftText, fontSize = 13.sp)
        Row(verticalAlignment = Alignment.CenterVertically) {
            IconButton(onClick = {}, modifier = Modifier.size(28.dp)) { Text("‹", color = DashboardColors.SoftText, fontSize = 22.sp) }
            repeat(5) { index ->
                val selected = index == 0
                Box(
                    modifier = Modifier
                        .padding(horizontal = 4.dp)
                        .size(28.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(if (selected) Color(0xFF1A3550) else Color.Transparent)
                        .border(1.dp, DashboardColors.BorderColor.copy(alpha = 0.3f), RoundedCornerShape(8.dp)),
                    contentAlignment = Alignment.Center
                ) {
                    Text((index + 1).toString(), color = if (selected) DashboardColors.LightText else DashboardColors.SoftText, fontSize = 12.sp)
                }
            }
            IconButton(onClick = {}, modifier = Modifier.size(28.dp)) { Text("›", color = DashboardColors.SoftText, fontSize = 22.sp) }
        }
    }
}

@Composable
private fun PreviewArtwork(type: PreviewType) {
    Canvas(modifier = Modifier.fillMaxSize()) {
        when (type) {
            PreviewType.BOAT -> {
                val base = size.height
                drawRect(color = Color(0xFF2C3136), topLeft = Offset(0f, base * 0.72f), size = Size(size.width, base * 0.28f))
                drawRoundRect(
                    color = Color(0xFF2D96FF),
                    topLeft = Offset(size.width * 0.17f, size.height * 0.48f),
                    size = Size(size.width * 0.42f, size.height * 0.18f),
                    cornerRadius = CornerRadius(20f, 20f)
                )
                drawRoundRect(
                    color = Color(0xFF0F9FF5),
                    topLeft = Offset(size.width * 0.32f, size.height * 0.36f),
                    size = Size(size.width * 0.2f, size.height * 0.15f),
                    cornerRadius = CornerRadius(16f, 16f)
                )
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.2f, size.height * 0.68f)
                        lineTo(size.width * 0.4f, size.height * 0.55f)
                        lineTo(size.width * 0.75f, size.height * 0.68f)
                        lineTo(size.width * 0.70f, size.height * 0.72f)
                        lineTo(size.width * 0.24f, size.height * 0.72f)
                        close()
                    },
                    color = Color(0xFF2B8AFA)
                )
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.18f, size.height * 0.72f)
                        lineTo(size.width * 0.08f, size.height * 0.82f)
                        lineTo(size.width * 0.22f, size.height * 0.82f)
                        close()
                    },
                    color = Color(0xFF0B6CC5)
                )
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.72f, size.height * 0.72f)
                        lineTo(size.width * 0.83f, size.height * 0.82f)
                        lineTo(size.width * 0.63f, size.height * 0.82f)
                        close()
                    },
                    color = Color(0xFF0B6CC5)
                )
            }
            PreviewType.CUBE -> {
                drawRect(color = Color(0xFF4F5358), topLeft = Offset(0f, size.height * 0.75f), size = Size(size.width, size.height * 0.25f))
                val sizeBox = Size(size.width * 0.46f, size.width * 0.36f)
                drawRoundRect(
                    color = Color(0xFFF0F3F7),
                    topLeft = Offset(size.width * 0.32f, size.height * 0.42f),
                    size = sizeBox,
                    cornerRadius = CornerRadius(12f, 12f),
                )
                drawRoundRect(
                    color = Color(0xFFCFD7E0),
                    topLeft = Offset(size.width * 0.42f, size.height * 0.28f),
                    size = Size(sizeBox.width * 0.68f, sizeBox.height * 0.7f),
                    cornerRadius = CornerRadius(12f, 12f),
                )
            }
            PreviewType.CAT -> {
                drawRect(color = Color(0xFF3B4550), topLeft = Offset(0f, size.height * 0.78f), size = Size(size.width, size.height * 0.22f))
                drawOval(color = Color(0xFF1E2328), topLeft = Offset(size.width * 0.3f, size.height * 0.42f), size = Size(size.width * 0.4f, size.height * 0.3f))
                drawOval(color = Color(0xFF0D1014), topLeft = Offset(size.width * 0.5f, size.height * 0.5f), size = Size(size.width * 0.06f, size.width * 0.06f))
                drawOval(color = Color(0xFF0D1014), topLeft = Offset(size.width * 0.4f, size.height * 0.5f), size = Size(size.width * 0.06f, size.width * 0.06f))
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.34f, size.height * 0.56f)
                        lineTo(size.width * 0.48f, size.height * 0.64f)
                        lineTo(size.width * 0.66f, size.height * 0.56f)
                    },
                    color = Color(0xFF0D1014),
                    style = Stroke(width = 2.dp.toPx())
                )
            }
            PreviewType.GEAR -> {
                val centerX = size.width * 0.5f
                val centerY = size.height * 0.55f
                for (i in 0..11) {
                    val angle = (i * 30f) * (kotlin.math.PI / 180f)
                    val x = centerX + kotlin.math.cos(angle).toFloat() * 82f
                    val y = centerY + kotlin.math.sin(angle).toFloat() * 82f
                    drawRoundRect(
                        color = Color(0xFF1A1C20),
                        topLeft = Offset(x - 16f, y - 16f),
                        size = Size(32f, 32f),
                        cornerRadius = CornerRadius(8f, 8f)
                    )
                }
                drawCircle(color = Color(0xFF0C1015), radius = 62f, center = Offset(centerX, centerY))
                drawCircle(color = Color(0xFFFAFBFC), radius = 42f, center = Offset(centerX, centerY))
            }
            PreviewType.DRAGON -> {
                drawOval(color = Color(0xFFEAEFF3), topLeft = Offset(size.width * 0.28f, size.height * 0.35f), size = Size(size.width * 0.45f, size.height * 0.45f))
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.34f, size.height * 0.42f)
                        lineTo(size.width * 0.56f, size.height * 0.26f)
                        lineTo(size.width * 0.68f, size.height * 0.4f)
                        lineTo(size.width * 0.56f, size.height * 0.56f)
                        close()
                    },
                    color = Color(0xFFEAEFF3)
                )
                drawPath(
                    path = Path().apply {
                        moveTo(size.width * 0.47f, size.height * 0.6f)
                        lineTo(size.width * 0.57f, size.height * 0.68f)
                        lineTo(size.width * 0.46f, size.height * 0.78f)
                    },
                    color = Color(0xFFB8C4D0),
                    style = Stroke(width = 4.dp.toPx())
                )
            }
        }
    }
}
